// packages/supabase-client/src/react/pages/UpdatePasswordPage.tsx

import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, TextField } from '@dopomogai/ui';
import { AuthLayout } from '../components/AuthLayout';
import { AuthAlert } from '../components';
import { useSupabaseClient, useUser, useAuthText, useAuthUI, useAuthErrorMessage } from '../hooks';

const MIN_PASSWORD_LENGTH = 8;

export const UpdatePasswordPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const supabase = useSupabaseClient();
  const user = useUser();
  const { text, resolveText, resolveString } = useAuthText();
  const { branding, layout, routes } = useAuthUI();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<React.ReactNode | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [success, setSuccess] = useState(false);
  const { resolveErrorMessage } = useAuthErrorMessage();

  const canSubmit = useMemo(
    () => password.length >= MIN_PASSWORD_LENGTH && password === confirmPassword,
    [password, confirmPassword]
  );

  const validate = useCallback((): string | null => {
    if (!password) return t('errors.passwordRequired', { defaultValue: 'Password is required.' });
    if (password.length < MIN_PASSWORD_LENGTH)
      return t('errors.passwordTooShort', {
        defaultValue: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
      });
    if (password !== confirmPassword)
      return t('errors.passwordsDoNotMatch', { defaultValue: 'Passwords do not match.' });
    return null;
  }, [password, confirmPassword, t]);

  const handleSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }
    setIsPending(true);
    const { error } = await supabase.auth.updateUser({ password });
    setIsPending(false);
    if (error) {
      setErrorMessage(resolveErrorMessage(error));
      return;
    }
    setSuccess(true);
  };

  // If there is no user after redirect, the link may be invalid/expired
  if (!user) {
    const brandingSubtitle = branding.brandingSubtitle ?? resolveText(text.brandingSubtitle, 'auth:branding.subtitle');
    const expiredSubtitle = resolveText(
      text.updatePasswordExpiredSubtitle,
      'auth:updatePasswordExpiredSubtitle',
      { defaultValue: 'Your reset link is invalid or expired. Please request a new one.' }
    );
    const backText = resolveText(text.backToLoginLink, 'auth:backToLoginLink', { defaultValue: 'Back to Sign In' });
    const backTextIsString = typeof backText === 'string' || typeof backText === 'number';
    const requestText = resolveText(text.sendResetLinkButton, 'auth:sendResetLinkButton', { defaultValue: 'Send reset link' });
    const requestTextIsString = typeof requestText === 'string' || typeof requestText === 'number';

    return (
      <AuthLayout
        title={resolveText(text.updatePasswordTitle, 'auth:updatePasswordTitle', { defaultValue: 'Update Password' })}
        subtitle={expiredSubtitle}
        brandingSubtitle={brandingSubtitle}
        brandingLogo={branding.logo}
        showBrandPanel={branding.showBrandPanel}
        variant={layout.variant}
        rootClassName={layout.rootClassName}
        brandPanelClassName={layout.brandPanelClassName}
        formPanelClassName={layout.formPanelClassName}
        cardClassName={layout.cardClassName}
        titleClassName={layout.titleClassName}
        subtitleClassName={layout.subtitleClassName}
      >
        <div className="space-y-4">
          <Button
            text={requestTextIsString ? String(requestText) : undefined}
            onClick={() => (window.location.href = routes.forgotPassword)}
            variant="primary"
          >
            {!requestTextIsString ? requestText : null}
          </Button>
          <Button
            text={backTextIsString ? String(backText) : undefined}
            onClick={() => (window.location.href = routes.login)}
            variant="outline"
          >
            {!backTextIsString ? backText : null}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  if (success) {
    const brandingSubtitle = branding.brandingSubtitle ?? resolveText(text.brandingSubtitle, 'auth:branding.subtitle');
    const continueText = resolveText(text.continue, 'auth:continue', { defaultValue: 'Continue' });
    const continueTextIsString = typeof continueText === 'string' || typeof continueText === 'number';

    return (
      <AuthLayout
        title={resolveText(text.passwordUpdatedTitle, 'auth:passwordUpdatedTitle', { defaultValue: 'Password updated' })}
        subtitle={resolveText(text.passwordUpdatedSubtitle, 'auth:passwordUpdatedSubtitle', {
          defaultValue: 'Your password has been updated. You can now continue to the app.',
        })}
        brandingSubtitle={brandingSubtitle}
        brandingLogo={branding.logo}
        showBrandPanel={branding.showBrandPanel}
        variant={layout.variant}
        rootClassName={layout.rootClassName}
        brandPanelClassName={layout.brandPanelClassName}
        formPanelClassName={layout.formPanelClassName}
        cardClassName={layout.cardClassName}
        titleClassName={layout.titleClassName}
        subtitleClassName={layout.subtitleClassName}
      >
        <div className="space-y-4">
          <Button
            text={continueTextIsString ? String(continueText) : undefined}
            onClick={() => (window.location.href = routes.appHome)}
            variant="primary"
          >
            {!continueTextIsString ? continueText : null}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  const brandingSubtitle = branding.brandingSubtitle ?? resolveText(text.brandingSubtitle, 'auth:branding.subtitle');
  const updateText = resolveText(text.updatePasswordButton, 'auth:updatePasswordButton', { defaultValue: 'Update Password' });
  const updateTextIsString = typeof updateText === 'string' || typeof updateText === 'number';

  return (
    <AuthLayout
      title={resolveText(text.updatePasswordTitle, 'auth:updatePasswordTitle', { defaultValue: 'Update Password' })}
      subtitle={resolveText(text.updatePasswordSubtitle, 'auth:updatePasswordSubtitle', { defaultValue: 'Set a new password for your account.' })}
      brandingSubtitle={brandingSubtitle}
      brandingLogo={branding.logo}
      showBrandPanel={branding.showBrandPanel}
      variant={layout.variant}
      rootClassName={layout.rootClassName}
      brandPanelClassName={layout.brandPanelClassName}
      formPanelClassName={layout.formPanelClassName}
      cardClassName={layout.cardClassName}
      titleClassName={layout.titleClassName}
      subtitleClassName={layout.subtitleClassName}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {errorMessage && <AuthAlert message={errorMessage} />}
        <TextField
          label={resolveString(text.newPasswordLabel, 'auth:newPasswordLabel', { defaultValue: 'New password' })}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={resolveString(text.newPasswordPlaceholder, 'auth:newPasswordPlaceholder', { defaultValue: 'Enter your new password' })}
          disabled={isPending}
        />
        <TextField
          label={resolveString(text.confirmPasswordLabel, 'auth:confirmPassword', { defaultValue: 'Confirm Password' })}
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder={resolveString(text.confirmPasswordPlaceholder, 'auth:confirmPasswordPlaceholder', { defaultValue: 'Re-enter your new password' })}
          disabled={isPending}
        />
        <Button
          type="submit"
          variant="primary"
          fullWidth
          text={updateTextIsString ? String(updateText) : undefined}
          disabled={isPending || !canSubmit}
          loading={isPending}
        >
          {!updateTextIsString ? updateText : null}
        </Button>
      </form>
    </AuthLayout>
  );
};
